import { useEffect, useMemo, useState } from 'react';
import {
  findOverdueSearchOrders,
  SEARCH_ALARM_WINDOW_MS,
  useSearchAlarmOrders,
  type OverdueSearchOrder,
} from '@/entities/order';

const MINUTE_MS = 60_000;
const TICK_MS = 1000;

/**
 * The night search alarm's data side (cpanel `useSearchAlarmMonitor`): polls
 * every in-search order over a rolling 24 h window ending "now" (rounded to
 * the minute so the query key — and the 10 s poll cadence — stays stable),
 * and re-derives the overdue set on a 1 s tick, so an order that crosses the
 * 4-minute line between polls still trips the alarm promptly and the shown
 * durations climb live.
 */
export function useSearchAlarm(enabled: boolean): OverdueSearchOrder[] {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!enabled) return;
    const id = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(id);
  }, [enabled]);

  const minute = Math.floor(now / MINUTE_MS) * MINUTE_MS;
  const range = useMemo(
    () => ({
      from_date: new Date(minute - SEARCH_ALARM_WINDOW_MS).toISOString(),
      till_date: new Date(minute).toISOString(),
    }),
    [minute],
  );

  const { data } = useSearchAlarmOrders(range, enabled);

  return useMemo(
    () => (enabled && data ? findOverdueSearchOrders(data.data, now) : []),
    [data, enabled, now],
  );
}
